import React, { useRef, useState, useEffect, useCallback } from "react";
import { Box, colors } from "../../../design-system";
import { FullscreenButton, PlayPauseButton } from "./InlineVideoPlayerButtons";

interface InlineVideoPlayerProps {
  videoUrl: string;
  onStop: () => void;
  /** Override the player height in px (default 85) — keeps it matched to the size slider. */
  heightPx?: number;
}

const InlineVideoPlayer: React.FC<InlineVideoPlayerProps> = ({ videoUrl, heightPx }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [paused, setPaused] = useState(true);

  const handleFullscreen = (): void => {
    const video = videoRef.current as any;
    if (!video) return;
    (video.requestFullscreen ?? video.webkitRequestFullscreen ?? video.webkitEnterFullscreen ?? video.mozRequestFullScreen)?.call(video);
  };

  const handlePlayPause = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().then(() => setPaused(false)).catch(() => {});
    } else {
      video.pause();
      setPaused(true);
    }
  };

  // Exiting fullscreen must reliably PAUSE the video. Before, the video looped forever, so
  // coming out of fullscreen left it playing/decoding nonstop — which froze the page (you
  // couldn't close it). Pausing on fullscreen-exit makes closing instant and clean.
  const handleFsChange = useCallback(() => {
    const fsEl =
      (document as any).fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement;
    if (!fsEl) {
      const video = videoRef.current;
      if (video) {
        video.pause();
        setPaused(true);
      }
    }
  }, []);

  // Auto-play as soon as the player opens. Opening it is itself a user click (swapping the
  // thumbnail for the player), so the browser allows playback — this makes it a single click
  // to play instead of "click to open, click again to play". If autoplay is blocked, it just
  // stays paused (no worse than before).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.play().then(() => setPaused(false)).catch(() => { /* autoplay blocked — stays paused */ });
  }, []);

  useEffect(() => {
    document.addEventListener("fullscreenchange", handleFsChange);
    document.addEventListener("webkitfullscreenchange", handleFsChange);
    document.addEventListener("mozfullscreenchange", handleFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
      document.removeEventListener("webkitfullscreenchange", handleFsChange);
      document.removeEventListener("mozfullscreenchange", handleFsChange);
      // On unmount, make sure nothing keeps playing/decoding in the background.
      const video = videoRef.current;
      if (video) {
        try { video.pause(); } catch (_) { /* ignore */ }
      }
    };
  }, [handleFsChange]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: heightPx ? `${heightPx}px` : "85px",
        backgroundColor: colors.background[900],
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        playsInline
        onEnded={() => setPaused(true)}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          backgroundColor: "#000",
        }}
        preload="metadata"
      />

      <PlayPauseButton paused={paused} onClick={handlePlayPause} />
      <FullscreenButton onClick={(e) => { e.stopPropagation(); handleFullscreen(); }} />
    </Box>
  );
};

export default InlineVideoPlayer;
